import { BacklogAging } from '../../hooks/useKPIs'

interface BacklogAgingBarProps {
  aging: BacklogAging
}

function formatEur(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

interface BucketRowProps {
  label: string
  amount: number
  pct: number         // 0–100
  color: string       // tailwind bg class
  severity: 'ok' | 'warn' | 'crit'
}

const severityText: Record<BucketRowProps['severity'], string> = {
  ok:   'text-emerald-400',
  warn: 'text-amber-400',
  crit: 'text-red-400',
}

function BucketRow({ label, amount, pct, color, severity }: BucketRowProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-[--km-text-secondary] font-medium">{label}</span>
        <span className={`tabular-nums font-semibold ${severityText[severity]}`}>
          {formatEur(amount)}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-[--km-surface-3] overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label}
        />
      </div>
      <p className="text-[10px] text-[--km-text-muted] text-right">{pct.toFixed(0)}% del backlog</p>
    </div>
  )
}

function EmptyState() {
  return (
    <p className="text-sm text-[--km-text-muted] py-4">Nessun dato di backlog disponibile.</p>
  )
}

export function BacklogAgingBar({ aging }: BacklogAgingBarProps) {
  const total = aging.bucket_0_30 + aging.bucket_31_90 + aging.bucket_90_plus

  if (total === 0) return <EmptyState />

  const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0)

  return (
    <div className="space-y-5" data-testid="backlog-aging-bar">
      <BucketRow
        label="0–30 gg"
        amount={aging.bucket_0_30}
        pct={pct(aging.bucket_0_30)}
        color="bg-emerald-500"
        severity="ok"
      />
      <BucketRow
        label="31–90 gg"
        amount={aging.bucket_31_90}
        pct={pct(aging.bucket_31_90)}
        color="bg-amber-500"
        severity="warn"
      />
      <BucketRow
        label="> 90 gg"
        amount={aging.bucket_90_plus}
        pct={pct(aging.bucket_90_plus)}
        color="bg-red-500"
        severity="crit"
      />
    </div>
  )
}
